import React from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Link as RouterLink } from 'react-router-dom'

import {
  Box, Divider, Link, Paper, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Typography,
} from '@mui/material'

// A link to a page of this site goes through the router, so following it
// doesn't reload the app; any other link is a plain one.
function MarkdownLink({ href, children }) {
  if (/^\/(?!\/)/.test(href || '')) {
    return <Link component={RouterLink} to={href}>{children}</Link>
  }
  return <Link href={href}>{children}</Link>
}

// Markdown in the app's MUI theme: each element maps to the component the rest
// of the app uses, so restyling the theme restyles the pages too. Raw HTML in
// the Markdown isn't rendered (react-markdown's default). Tables and
// strikethrough are GitHub's extensions (remark-gfm).
const components = {
  h1: ({ children }) => <Typography variant='h4' component='h2' sx={{ mt: 4, mb: 2 }}>{children}</Typography>,
  h2: ({ children }) => <Typography variant='h5' component='h2' sx={{ mt: 4, mb: 2 }}>{children}</Typography>,
  h3: ({ children }) => <Typography variant='h6' component='h3' sx={{ mt: 3, mb: 1 }}>{children}</Typography>,
  p: ({ children }) => <Typography variant='body1' sx={{ mb: 2 }}>{children}</Typography>,
  a: MarkdownLink,
  ul: ({ children }) => <Box component='ul' sx={{ mt: 0, mb: 2, pl: 3 }}>{children}</Box>,
  ol: ({ children }) => <Box component='ol' sx={{ mt: 0, mb: 2, pl: 3 }}>{children}</Box>,
  li: ({ children }) => <Typography component='li' variant='body1'>{children}</Typography>,
  hr: () => <Divider sx={{ my: 3 }} />,
  // A wide table scrolls sideways on a phone rather than widening the page.
  table: ({ children }) => (
    <TableContainer component={Paper} variant='outlined' sx={{ mb: 2 }}>
      <Table size='small'>{children}</Table>
    </TableContainer>
  ),
  thead: ({ children }) => <TableHead>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => <TableCell sx={{ fontWeight: 'bold' }}>{children}</TableCell>,
  td: ({ children }) => <TableCell>{children}</TableCell>,
  // Images live in public/articles/<collection>/<slug>/, linked as
  // /articles/<collection>/<slug>/<file>. Never wider than the text. The
  // title holds layout words, not a tooltip: "half" shows it at half the
  // text's width (full width on a phone), centred, or at the left with
  // "half left" (`![alt](src "half left")`).
  img: ({ src, alt, title }) => {
    const layout = (title || '').split(/\s+/)
    return (
      <Box component='img' src={src} alt={alt || ''}
        sx={{
          display: 'block', maxWidth: '100%', height: 'auto', my: 2,
          ...(layout.includes('half') && { width: { xs: '100%', sm: '50%' }, mx: 'auto' }),
          ...(layout.includes('left') && { ml: 0, mr: 'auto' }),
        }} />
    )
  },
}

export default function Markdown({ children }) {
  return <ReactMarkdown remarkPlugins={[remarkGfm]} components={components}>{children}</ReactMarkdown>
}
